import React, { useState, useEffect } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Drawer from "@material-ui/core/Drawer";
import Link from "@material-ui/core/Link";
import InputLabel from "@material-ui/core/InputLabel";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import CircularProgress from "@material-ui/core/CircularProgress";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import ContactModal from "../ContactModal";
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import MarkdownWrapper from "../MarkdownWrapper";

// Debe coincidir con `drawerWidth` en components/Ticket/index.js y
// `CONTACT_DRAWER_WIDTH` en pages/Kanban/index.js (ambos calculan el
// margen de "empuje" del chat en base a este mismo ancho).
const drawerWidth = 380;

const useStyles = makeStyles(theme => ({
	drawer: {
		width: drawerWidth,
		flexShrink: 0,
	},
	drawerPaper: {
		width: drawerWidth,
		display: "flex",
		borderTop: "1px solid rgba(0, 0, 0, 0.12)",
		borderRight: "1px solid rgba(0, 0, 0, 0.12)",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		borderTopRightRadius: 4,
		borderBottomRightRadius: 4,
	},
	header: {
		display: "flex",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		backgroundColor: "#eee",
		alignItems: "center",
		padding: theme.spacing(0, 1),
		minHeight: "73px",
		justifyContent: "flex-start",
	},
	content: {
		display: "flex",
		backgroundColor: "#eee",
		flexDirection: "column",
		padding: "8px 0px 8px 8px",
		height: "100%",
		overflowY: "auto",
		...theme.scrollbarStyles,
	},

	contactAvatar: {
		margin: 15,
		width: 160,
		height: 160,
	},

	contactHeader: {
		display: "flex",
		padding: 8,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		flexShrink: 0,
		"& > *": {
			margin: 4,
		},
	},

	contactDetails: {
		marginTop: 8,
		padding: 8,
		display: "flex",
		flexDirection: "column",
	},
	contactExtraInfo: {
		marginTop: 4,
		padding: 6,
	},

	tabsPanel: {
		marginTop: 8,
		display: "flex",
		flexDirection: "column",
	},
	docTabs: {
		minHeight: 32,
		borderBottom: "1px solid rgba(0,0,0,0.08)",
	},
	docTab: {
		minHeight: 32,
		minWidth: "auto",
		padding: "6px 12px",
		fontSize: 11,
	},
	scrollableList: {
		padding: 8,
		maxHeight: 340,
		overflowY: "auto",
		...theme.scrollbarStyles,
	},
	inkaMoneyLoading: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		padding: "4px 0",
	},
	inkaMoneyMessage: {
		fontSize: 12,
		color: "#888",
		padding: "4px 0",
	},
	inkaMoneyProducto: {
		padding: "8px 0",
		borderBottom: "1px solid rgba(0,0,0,0.08)",
		"&:last-child": {
			borderBottom: "none",
		},
	},
	inkaMoneyProductoFecha: {
		fontSize: 12,
		fontWeight: 600,
		color: "#555",
	},
	inkaMoneyProductoLine: {
		fontSize: 14,
	},
	inkaMoneyProductoImei: {
		fontSize: 11,
		color: "#999",
	},
	inkaMoneyOrden: {
		padding: "8px 0",
		borderBottom: "1px solid rgba(0,0,0,0.08)",
		"&:last-child": {
			borderBottom: "none",
		},
	},
	inkaMoneyOrdenItem: {
		fontSize: 11,
		color: "#666",
		paddingLeft: 8,
	},
	yearSummary: {
		display: "flex",
		flexWrap: "wrap",
		alignItems: "center",
		gap: 6,
		padding: "6px 10px",
		fontSize: 12,
		color: "#444",
		backgroundColor: "#f5f5f5",
		borderBottom: "1px solid rgba(0,0,0,0.08)",
	},
	yearSummarySeparator: {
		color: "#bbb",
	},
}));

const InkaMoneyState = ({ classes, contact, loading, error, data, emptyKey }) => {
	if (!contact?.documents?.length) {
		return (
			<Typography className={classes.inkaMoneyMessage}>
				{i18n.t("contactDrawer.inkamoney.noDocument")}
			</Typography>
		);
	}
	if (loading) {
		return (
			<div className={classes.inkaMoneyLoading}>
				<CircularProgress size={14} />
				<Typography className={classes.inkaMoneyMessage} component="span">
					{i18n.t("contactDrawer.inkamoney.loading")}
				</Typography>
			</div>
		);
	}
	if (error) {
		return (
			<Typography className={classes.inkaMoneyMessage}>
				{i18n.t("contactDrawer.inkamoney.error")}
			</Typography>
		);
	}
	if (!data?.found) {
		return (
			<Typography className={classes.inkaMoneyMessage}>
				{i18n.t("contactDrawer.inkamoney.notFound")}
			</Typography>
		);
	}
	if (!data[emptyKey]?.length) {
		return (
			<Typography className={classes.inkaMoneyMessage}>
				{i18n.t("contactDrawer.inkamoney.empty")}
			</Typography>
		);
	}
	return null;
};

// Fecha (string "YYYY-MM-DD") de la orden más reciente de un documento de
// InkaMoney, o null si no tiene órdenes. Se usa para decidir qué documento
// (RUC/DNI) mostrar primero cuando el contacto tiene varios.
const getLatestOrderDate = inkaDoc => {
	if (!inkaDoc?.found || !inkaDoc?.ordenes?.length) return null;
	return inkaDoc.ordenes.reduce(
		(latest, orden) =>
			orden.fecha && (!latest || orden.fecha > latest) ? orden.fecha : latest,
		null
	);
};

const ContactDrawer = ({ open, handleDrawerClose, contact, loading }) => {
	const classes = useStyles();

	const [modalOpen, setModalOpen] = useState(false);
	const [activeTab, setActiveTab] = useState("ordenes");
	const [activeDocIndex, setActiveDocIndex] = useState(0);
	const [inkaLoading, setInkaLoading] = useState(false);
	const [inkaData, setInkaData] = useState(null);

	const documentsKey = contact?.documents?.map(d => d.document).join(",") || "";

	useEffect(() => {
		if (!contact?.id || !contact?.documents?.length) {
			setInkaData(null);
			return;
		}

		let ignore = false;
		setInkaLoading(true);

		(async () => {
			try {
				const { data } = await api.get(`/contacts/${contact.id}/inkamoney`);
				if (!ignore) setInkaData(data);
			} catch (err) {
				if (!ignore) setInkaData(null);
			} finally {
				if (!ignore) setInkaLoading(false);
			}
		})();

		return () => {
			ignore = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contact?.id, documentsKey]);

	useEffect(() => {
		setActiveTab("ordenes");
		setActiveDocIndex(0);
	}, [contact?.id]);

	// Al llegar los datos de InkaMoney, si el contacto tiene varios
	// documentos (RUC/DNI) se selecciona por defecto el que tenga la orden
	// con fecha más reciente, en vez de asumir siempre el primero.
	useEffect(() => {
		if (!contact?.documents?.length || !inkaData?.documents?.length) return;

		const indices = contact.documents.map((_, index) => index);
		indices.sort((a, b) => {
			const dateA = getLatestOrderDate(inkaData.documents[a]);
			const dateB = getLatestOrderDate(inkaData.documents[b]);
			if (dateA && dateB) return dateB.localeCompare(dateA);
			if (dateA) return -1;
			if (dateB) return 1;
			return a - b;
		});

		if (indices.length) setActiveDocIndex(indices[0]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [inkaData]);

	const currentDoc = inkaData?.documents?.[activeDocIndex];

	const productos = currentDoc?.found
		? [...(currentDoc.productos || [])].sort((a, b) =>
				(b.fecha_alta || "").localeCompare(a.fecha_alta || "")
		  )
		: [];

	const ordenes = currentDoc?.found
		? [...(currentDoc.ordenes || [])].sort((a, b) =>
				(b.fecha || "").localeCompare(a.fecha || "")
		  )
		: [];

	// Mismo orden usado para elegir el documento por defecto, pero aplicado
	// también a cómo se dibujan las pestañas de documento.
	const sortedDocIndices = (contact?.documents || []).map((_, index) => index);
	sortedDocIndices.sort((a, b) => {
		const dateA = getLatestOrderDate(inkaData?.documents?.[a]);
		const dateB = getLatestOrderDate(inkaData?.documents?.[b]);
		if (dateA && dateB) return dateB.localeCompare(dateA);
		if (dateA) return -1;
		if (dateB) return 1;
		return a - b;
	});

	// Resumen rápido de órdenes del año actual y el anterior, para priorizar
	// atención a clientes con más actividad reciente.
	const formatSoles = value => {
		const fixed = value.toFixed(2);
		return fixed.endsWith(".00") ? fixed.slice(0, -3) : fixed;
	};

	const currentYear = new Date().getFullYear();
	const yearSummary = [currentYear, currentYear - 1].map(year => {
		const yearOrdenes = ordenes.filter(
			orden => String(orden.fecha || "").slice(0, 4) === String(year)
		);
		const total = yearOrdenes.reduce(
			(sum, orden) => sum + (parseFloat(orden.total) || 0),
			0
		);
		const unidades = yearOrdenes.reduce(
			(sum, orden) =>
				sum +
				(orden.items || []).reduce(
					(itemSum, item) => itemSum + (parseFloat(item.cantidad) || 0),
					0
				),
			0
		);
		return { year, count: yearOrdenes.length, total, unidades };
	});

	// Notas sin fecha (tipo "Nota") van siempre arriba, ordenadas por más
	// reciente creada primero; debajo, las notas con fecha, de la más
	// reciente a la más antigua.
	const extraInfo = [...(contact?.extraInfo || [])].sort((a, b) => {
		if (!a.name && !b.name) return (b.id || 0) - (a.id || 0);
		if (!a.name) return -1;
		if (!b.name) return 1;
		return (b.name || "").localeCompare(a.name || "");
	});

	return (
		<Drawer
			className={classes.drawer}
			variant="persistent"
			anchor="right"
			open={open}
			PaperProps={{ style: { position: "absolute" } }}
			BackdropProps={{ style: { position: "absolute" } }}
			ModalProps={{
				container: document.getElementById("drawer-container"),
				style: { position: "absolute" },
			}}
			classes={{
				paper: classes.drawerPaper,
			}}
		>
			<div className={classes.header}>
				<IconButton onClick={handleDrawerClose}>
					<CloseIcon />
				</IconButton>
				<Typography style={{ justifySelf: "center" }}>
					{i18n.t("contactDrawer.header")}
				</Typography>
			</div>
			{loading ? (
				<ContactDrawerSkeleton classes={classes} />
			) : (
				<div className={classes.content}>
					<Paper square variant="outlined" className={classes.contactHeader}>
						<Avatar
							alt={contact.name}
							src={contact.profilePicUrl}
							className={classes.contactAvatar}
						></Avatar>

						<Typography>{contact.name}</Typography>
						<Typography>
							<Link href={`tel:${contact.number}`}>{contact.number}</Link>
						</Typography>
						<Button
							variant="outlined"
							color="primary"
							onClick={() => setModalOpen(true)}
						>
							{i18n.t("contactDrawer.buttons.edit")}
						</Button>
					</Paper>

					<ContactModal
						open={modalOpen}
						onClose={() => setModalOpen(false)}
						contactId={contact.id}
					></ContactModal>

					<Paper square variant="outlined" className={classes.tabsPanel}>
						{contact?.documents?.length > 1 && (
							<Tabs
								value={activeDocIndex}
								onChange={(e, value) => setActiveDocIndex(value)}
								variant="scrollable"
								scrollButtons="auto"
								indicatorColor="secondary"
								textColor="secondary"
								className={classes.docTabs}
							>
								{sortedDocIndices.map(index => {
									const doc = contact.documents[index];
									return (
										<Tab
											key={doc.id || index}
											value={index}
											label={doc.document}
											className={classes.docTab}
										/>
									);
								})}
							</Tabs>
						)}

						{currentDoc?.found && (
							<div className={classes.yearSummary}>
								{yearSummary.map((y, index) => (
									<React.Fragment key={y.year}>
										{index > 0 && (
											<span className={classes.yearSummarySeparator}>|</span>
										)}
										<span>
											<strong>{y.year}</strong> ({y.count} ord) (S/.
											{formatSoles(y.total)}) {y.unidades}u
										</span>
									</React.Fragment>
								))}
							</div>
						)}

						<Tabs
							value={activeTab}
							onChange={(e, value) => setActiveTab(value)}
							variant="fullWidth"
							indicatorColor="primary"
							textColor="primary"
						>
							<Tab
								value="ordenes"
								label={i18n.t("contactDrawer.inkamoney.ordenes")}
							/>
							<Tab
								value="productos"
								label={i18n.t("contactDrawer.inkamoney.productos")}
							/>
						</Tabs>

						{activeTab === "productos" && (
							<div className={classes.scrollableList}>
								<InkaMoneyState
									classes={classes}
									contact={contact}
									loading={inkaLoading}
									error={currentDoc?.error}
									data={currentDoc}
									emptyKey="productos"
								/>
								{productos.map((producto, index) => (
									<div
										key={`${producto.serie || producto.imei || index}`}
										className={classes.inkaMoneyProducto}
									>
										<Typography className={classes.inkaMoneyProductoFecha}>
											{producto.fecha_alta}
										</Typography>
										<Typography className={classes.inkaMoneyProductoLine}>
											{producto.producto_corto || producto.producto}
											{producto.serie ? ` | ${producto.serie}` : ""}{" "}
											<span
												style={{
													fontWeight: 600,
													color: producto.activo ? "#2e7d32" : "#9e9e9e",
												}}
											>
												({producto.activo
													? i18n.t("contactDrawer.inkamoney.activo")
													: i18n.t("contactDrawer.inkamoney.inactivo")}
												)
											</span>
										</Typography>
										{producto.imei && (
											<Typography className={classes.inkaMoneyProductoImei}>
												IMEI: {producto.imei}
											</Typography>
										)}
									</div>
								))}
							</div>
						)}

						{activeTab === "ordenes" && (
							<div className={classes.scrollableList}>
								<InkaMoneyState
									classes={classes}
									contact={contact}
									loading={inkaLoading}
									error={currentDoc?.error}
									data={currentDoc}
									emptyKey="ordenes"
								/>
								{currentDoc?.found &&
									ordenes.map(orden => (
										<div key={orden.codigo} className={classes.inkaMoneyOrden}>
											<Typography style={{ fontSize: 13 }}>
												{orden.codigo} — {orden.fecha} — S/ {orden.total}
											</Typography>
											{orden.items?.map((item, idx) => (
												<Typography key={idx} className={classes.inkaMoneyOrdenItem}>
													{item.cantidad}x {item.producto} @ S/ {item.precio_unitario}
												</Typography>
											))}
										</div>
									))}
							</div>
						)}

					</Paper>

					<Paper square variant="outlined" className={classes.contactDetails}>
						<Typography variant="subtitle1">
							{i18n.t("contactDrawer.extraInfo")}
						</Typography>
						<div className={classes.scrollableList}>
							{!extraInfo.length && (
								<Typography className={classes.inkaMoneyMessage}>
									{i18n.t("contactDrawer.inkamoney.empty")}
								</Typography>
							)}
							{extraInfo.map(info => (
								<Paper
									key={info.id}
									square
									variant="outlined"
									className={classes.contactExtraInfo}
								>
									{/* Fecha si la nota es fechada; si no, se marca como "Nota" */}
									<InputLabel
										style={{
											fontSize: 11,
											marginBottom: 2,
											color: info.name ? "#888" : "#1565c0",
											fontWeight: info.name ? 400 : 600,
										}}
									>
										{info.name || "Nota"}
									</InputLabel>
									{/* Valor completo, sin truncar, letra pequeña */}
									<Typography
										component="div"
										style={{
											paddingTop: 2,
											fontSize: 12,
											whiteSpace: "pre-wrap",
											wordBreak: "break-word",
											lineHeight: 1.5,
										}}
									>
										<MarkdownWrapper>{info.value}</MarkdownWrapper>
									</Typography>
								</Paper>
							))}
						</div>
					</Paper>
				</div>
			)}
		</Drawer>
	);
};

export default ContactDrawer;
